import { typographyVariant } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { useReducedMotion } from 'motion/react';
import { useIntl, type MessageDescriptor } from 'react-intl';

import { useRotatingPlaceholder } from './useRotatingPlaceholder';

interface RotatingComposerPlaceholderProps {
  lines: readonly MessageDescriptor[];
  /** Holds the current line while the user is in the editor. */
  paused?: boolean;
}

/**
 * Visible-only placeholder that cycles through the composer hints. It is
 * `aria-hidden`: the textbox keeps one stable accessible name, so a screen
 * reader is not re-announced every few seconds.
 *
 * Every line is stacked in the same grid cell and only opacity/transform
 * change, so the outgoing and incoming lines crossfade with no remount and no
 * layout shift.
 */
export const RotatingComposerPlaceholder = ({
  lines,
  paused = false,
}: RotatingComposerPlaceholderProps) => {
  const intl = useIntl();
  const reducedMotion = useReducedMotion() ?? false;
  const { activeIndex, previousIndex } = useRotatingPlaceholder({
    count: lines.length,
    paused,
    reducedMotion,
  });

  return (
    <span
      aria-hidden="true"
      data-testid="composer-rotating-placeholder"
      className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)]"
    >
      {lines.map((line, index) => {
        const isActive = index === activeIndex;
        return (
          <span
            key={line.id}
            data-active={isActive ? 'true' : undefined}
            className={cn(
              typographyVariant({ variant: 'p2' }),
              'col-start-1 row-start-1 block min-w-0 truncate text-text-secondary',
              'transition-[opacity,transform] duration-700 ease-in-out-cubic',
              'motion-reduce:transform-none motion-reduce:transition-none',
              isActive
                ? 'translate-y-0 opacity-100'
                : index === previousIndex
                  ? '-translate-y-0.5 opacity-0'
                  : 'translate-y-0.5 opacity-0'
            )}
          >
            {intl.formatMessage(line)}
          </span>
        );
      })}
    </span>
  );
};
